import {noop} from '@benchling/js-utils';
import {Modal, UnstyledButton, createStyles} from '@mantine/core';
import {useDisclosure, useHotkeys} from '@mantine/hooks';
import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';

import {trackEvent} from 'analytics/Analytics';
import BenchlingConfig from 'BenchlingConfig';
import {OMNIBOX_ANALYTICS_ACTIONS} from 'constants/analytics-products/OmniboxAnalyticsConstants';
import FileBrowserActions from 'file-browser/FileBrowserActions';
import type {FileSummary} from 'folder-items/FileConstants';
import {AgentType, ProcessTaskStatusType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {QueryResultType, useBenchlingQuery} from 'graphql-client/BenchlingQuery';
import {openDraftChatInSidepanel} from 'llm/chat-library/sidepanel/ChatRouterUtil';
import {isAgentTypeEnabled} from 'llm/chat-library/sidepanel/ChatTypeUtil';
import {showCreateEntryFromTemplateModal} from 'nav/CreateDropdownActions';
import AnimatedBorderOverlay from 'omnibox/AnimatedBorderOverlay';
import OmniboxAction from 'omnibox/OmniboxAction';
import type {OmniboxActionItem} from 'omnibox/OmniboxAction';
import {fetchFilteredRecent, fetchRecentItems, streamOmniboxQuery} from 'omnibox/OmniboxDAO';
import type {FilteredRecentItem} from 'omnibox/OmniboxDAO';
import OmniboxFilterTabs from 'omnibox/OmniboxFilterTabs';
import type {FilterTab} from 'omnibox/OmniboxFilterTabs';
import OmniboxFooter from 'omnibox/OmniboxFooter';
import OmniboxQuickActions from 'omnibox/OmniboxQuickActions';
import OmniboxSearchInput from 'omnibox/OmniboxSearchInput';
import useOmniboxKeyboardNav from 'omnibox/useOmniboxKeyboardNav';
import useOmniboxStream from 'omnibox/useOmniboxStream';
import type {OmniboxFilter} from 'omnibox/useTypeaheadData';
import useTypeaheadData from 'omnibox/useTypeaheadData';
import {logProfilerTimingEvent} from 'performance/BenchlingProfiler';
import {
  cleanUpOmniboxSearchProfilerEvents,
  OMNIBOX_SEARCH_RESULTS_RENDERED_EVENT,
  OMNIBOX_SEARCH_STARTED_EVENT,
} from 'performance/processSearchEvents';
import type {
  AssignedProcessTasksQuery,
  AssignedProcessTasksQueryVariables,
} from 'processes/home/__generated/AssignedProcessTasksQuery';
import {
  ASSIGNED_PROCESS_TASKS_QUERY,
  setTaskSearchFilter,
} from 'processes/home/AssignedProcessTaskStatusSummary';
import WorkspaceStore from 'sidebar/WorkspaceStore';
import Chip, {ChipSize} from 'ui-library/chips/Chip';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';

import {abortAttachmentPreview, buildActions, buildDefaultActions} from './omniboxActionBuilders';
import type {FilterMode} from './omniboxConstants';
import {
  FILTER_ACTION_META,
  FILTER_MODE_PLACEHOLDERS,
  NOTHING_FOUND_MESSAGES,
  UNIFIED_MAX_PER_CATEGORY,
} from './omniboxConstants';
import {
  extractFilterParams,
  filterAndSearchTypeahead,
  getFilterChipLabel,
  getFilterDescription,
} from './omniboxFilterUtils';

const useStyles = createStyles((theme) => ({
  contentWrapper: {
    display: 'flex',
    flexDirection: 'column' as const,
    overflow: 'hidden',
  },
  filterChipsRow: {
    display: 'flex',
    flexWrap: 'wrap' as const,
    alignItems: 'center',
    gap: 4,
    padding: '8px 16px 4px',
    borderBottom: '1px solid #e9ecef',
  },
  clearAllButton: {
    fontSize: 12,
    fontWeight: 500,
    color: theme.colors.gray[6],
    padding: '2px 8px',
    borderRadius: 4,
    transition: 'color 100ms ease, background-color 100ms ease',
    '&:hover': {
      color: theme.colors.gray[7],
      backgroundColor: theme.colors.gray[0],
    },
  },
  scrollArea: {
    flex: 1,
    overflowY: 'auto' as const,
    minHeight: 0,
    paddingBottom: 4,
  },
  errorMessage: {
    padding: 16,
    textAlign: 'center' as const,
    color: '#e03131',
    fontSize: 14,
  },
  emptyMessage: {
    padding: 16,
    textAlign: 'center' as const,
    color: '#868e96',
    fontSize: 14,
  },
  srOnly: {
    position: 'absolute' as const,
    width: 1,
    height: 1,
    padding: 0,
    margin: -1,
    overflow: 'hidden',
    clipPath: 'inset(50%)',
    whiteSpace: 'nowrap' as const,
    border: 0,
  },
  sectionHeader: {
    fontSize: 11,
    fontWeight: 600,
    textTransform: 'uppercase' as const,
    letterSpacing: '0.05em',
    color: '#868e96',
    padding: '12px 16px 4px',
  },
}));

export default function Omnibox(): JSX.Element {
  const {classes} = useStyles();
  const [query, setQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const [copiedAnswer, setCopiedAnswer] = useState(false);
  const [recentItems, setRecentItems] = useState<Array<FileSummary>>([]);
  const [showCompletionFlash, setShowCompletionFlash] = useState(false);
  const [selectedFilters, setSelectedFilters] = useState<Array<OmniboxFilter>>([]);
  const [filterMode, setFilterMode] = useState<FilterMode | null>(null);
  const [typeaheadQuery, setTypeaheadQuery] = useState('');
  const [filteredRecentItems, setFilteredRecentItems] = useState<Array<FilteredRecentItem>>([]);
  const [filteredRecentLoading, setFilteredRecentLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [lastRequestId, setLastRequestId] = useState<string | null>(null);
  const [opened, {close, toggle}] = useDisclosure(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const mountedRef = useRef(true);
  const queriesSubmittedRef = useRef(0);
  useEffect(
    () => () => {
      mountedRef.current = false;
    },
    []
  );

  // Typeahead data: schema/project/user preloading and Fuse instances
  const {typeaheadItems, projectFolderItems, userItems, fuse, projectFuse, userFuse} = useTypeaheadData();

  // Derive active tab from filter mode
  const activeTab: FilterTab = filterMode == null ? 'all' : filterMode;

  // Streaming omnibox results via Ably
  const {results, setResults, helpDocsResults, setHelpDocsResults, requestIdRef} = useOmniboxStream();

  // Refs to avoid recreating handleKeyDownCapture on every results/loading change
  const resultsRef = useRef(results);
  resultsRef.current = results;
  const loadingRef = useRef(loading);
  loadingRef.current = loading;
  // Tracks whether the user has submitted a query whose results are being shown.
  // Used to determine if filter changes should trigger an automatic re-query.
  const lastSubmittedQueryRef = useRef<string | null>(null);
  const usedArrowKeysRef = useRef(false);

  // Register keyboard shortcut to open/close the palette
  // Pass empty tagsToIgnore + triggerOnContentEditable so it fires from inputs/textareas
  useHotkeys([['mod+/', toggle]], [], true);

  // Auto-focus input when opened
  useEffect(() => {
    if (opened) {
      queriesSubmittedRef.current = 0;
      // Allow resubmitting the same query in a new session
      lastSubmittedQueryRef.current = null;
      trackEvent(OMNIBOX_ANALYTICS_ACTIONS.OPENED_OMNIBOX);
      requestAnimationFrame(() => {
        inputRef.current?.focus();
      });
    }
  }, [opened]);

  const taskQuery = useBenchlingQuery<AssignedProcessTasksQuery, AssignedProcessTasksQueryVariables>(
    ASSIGNED_PROCESS_TASKS_QUERY,
    {
      variables: {statusTypes: [ProcessTaskStatusType.PENDING, ProcessTaskStatusType.IN_PROGRESS]},
    }
  );

  useEffect(() => {
    fetchRecentItems()
      .then((response) => {
        if (mountedRef.current) {
          setRecentItems(response.data);
        }
      })
      .catch((e) => {
        // eslint-disable-next-line no-console
        console.error('Failed to fetch recent items', e);
      });
  }, []);

  // Fetch filtered recent items when filters are active but no query text
  useEffect(() => {
    if (!selectedFilters.length || query || filterMode != null) {
      setFilteredRecentItems([]);
      setFilteredRecentLoading(false);
      return;
    }

    const {schemaApiIds, objectTypes, projectApiIds, folderApiIds, authorApiIds} =
      extractFilterParams(selectedFilters);

    let cancelled = false;
    setFilteredRecentLoading(true);

    fetchFilteredRecent({
      schemaApiIds: schemaApiIds.length > 0 ? schemaApiIds : undefined,
      objectTypes: objectTypes.length > 0 ? objectTypes : undefined,
      projectApiIds: projectApiIds.length > 0 ? projectApiIds : undefined,
      folderApiIds: folderApiIds.length > 0 ? folderApiIds : undefined,
      authorApiIds: authorApiIds.length > 0 ? authorApiIds : undefined,
    })
      .then((items) => {
        if (!cancelled && mountedRef.current) {
          setFilteredRecentItems(items);
          setFilteredRecentLoading(false);
        }
      })
      .catch(() => {
        if (!cancelled && mountedRef.current) {
          setFilteredRecentItems([]);
          setFilteredRecentLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [selectedFilters, query, filterMode]);

  useEffect(() => {
    if (!copiedAnswer) {
      return undefined;
    }
    const timer = setTimeout(() => {
      setCopiedAnswer(false);
    }, 2000);
    return () => {
      clearTimeout(timer);
    };
  }, [copiedAnswer]);

  const handleCopyAnswer = useCallback(() => {
    setCopiedAnswer(true);
  }, []);

  const taskData = taskQuery.type === QueryResultType.SUCCESS ? taskQuery.data : undefined;

  // Flash a brief completion indicator when loading finishes with results
  const prevLoadingRef = useRef(false);
  useEffect(() => {
    if (prevLoadingRef.current && !loading && results) {
      setShowCompletionFlash(true);
      const timer = setTimeout(() => {
        setShowCompletionFlash(false);
      }, 600);
      return () => {
        clearTimeout(timer);
      };
    }
    prevLoadingRef.current = loading;
    return undefined;
  }, [loading, results]);

  const defaultActions = useMemo(() => buildDefaultActions(taskData, recentItems), [taskData, recentItems]);

  const exitFilterMode = useCallback(() => {
    setFilterMode(null);
    setTypeaheadQuery('');
  }, []);

  const handleClearAllFilters = useCallback(() => {
    trackEvent(OMNIBOX_ANALYTICS_ACTIONS.CLEARED_ALL_FILTERS, {
      filterCount: selectedFilters.length,
    });
    setSelectedFilters([]);
  }, [selectedFilters.length]);

  const selectedFilterIds = useMemo(
    () => new Set(selectedFilters.map((f) => `${f.kind}:${f.id}`)),
    [selectedFilters]
  );

  // Build typeahead actions based on current filter mode
  const typeaheadActions = useMemo((): Array<OmniboxActionItem> => {
    if (filterMode == null) {
      return [];
    }

    const buildFilterActions = (items: Array<OmniboxFilter>): Array<OmniboxActionItem> =>
      items.map((item) => {
        const meta = FILTER_ACTION_META[item.kind];
        return {
          id: `filter-${item.kind}-${item.id}`,
          title: item.name,
          description: getFilterDescription(item),
          resultType: meta.resultType,
          modelType: item.modelType,
          closeOnTrigger: false,
          onTrigger: () => {
            trackEvent(OMNIBOX_ANALYTICS_ACTIONS.ADDED_FILTER, {filterKind: item.kind});
            setSelectedFilters((prev) => [...prev, item]);
            exitFilterMode();
            inputRef.current?.focus();
          },
        };
      });

    if (filterMode === 'type') {
      return buildFilterActions(
        filterAndSearchTypeahead(typeaheadItems, fuse, typeaheadQuery, selectedFilterIds)
      );
    }

    if (filterMode === 'project') {
      return buildFilterActions(
        filterAndSearchTypeahead(projectFolderItems, projectFuse, typeaheadQuery, selectedFilterIds)
      );
    }

    if (filterMode === 'user') {
      return buildFilterActions(
        filterAndSearchTypeahead(userItems, userFuse, typeaheadQuery, selectedFilterIds)
      );
    }

    // Unified mode — search all categories
    return [
      ...buildFilterActions(
        filterAndSearchTypeahead(
          typeaheadItems,
          fuse,
          typeaheadQuery,
          selectedFilterIds,
          UNIFIED_MAX_PER_CATEGORY
        )
      ),
      ...buildFilterActions(
        filterAndSearchTypeahead(
          projectFolderItems,
          projectFuse,
          typeaheadQuery,
          selectedFilterIds,
          UNIFIED_MAX_PER_CATEGORY
        )
      ),
      ...buildFilterActions(
        filterAndSearchTypeahead(
          userItems,
          userFuse,
          typeaheadQuery,
          selectedFilterIds,
          UNIFIED_MAX_PER_CATEGORY
        )
      ),
    ];
  }, [
    exitFilterMode,
    filterMode,
    typeaheadQuery,
    typeaheadItems,
    fuse,
    projectFolderItems,
    projectFuse,
    userItems,
    userFuse,
    selectedFilterIds,
  ]);

  const totalFilterOptionCount = useMemo((): number | undefined => {
    if (filterMode == null) {
      return undefined;
    }
    const countAvailable = (items: Array<OmniboxFilter>): number =>
      items.filter((item) => !selectedFilterIds.has(`${item.kind}:${item.id}`)).length;
    if (filterMode === 'type') {
      return countAvailable(typeaheadItems);
    }
    if (filterMode === 'project') {
      return countAvailable(projectFolderItems);
    }
    if (filterMode === 'user') {
      return countAvailable(userItems);
    }
    return countAvailable(typeaheadItems) + countAvailable(projectFolderItems) + countAvailable(userItems);
  }, [filterMode, selectedFilterIds, typeaheadItems, projectFolderItems, userItems]);

  const filteredRecentActions = useMemo(
    (): Array<OmniboxActionItem> =>
      filteredRecentItems.map((item) => ({
        id: `filtered-recent-${item.apiId}`,
        title: item.name,
        group: 'Recently Modified',
        resultType: 'filtered_recent_item',
        objectType: item.objectType,
        apiId: item.apiId,
        modifiedAt: item.modifiedAt ?? undefined,
        onTrigger: () => {
          FileBrowserActions.openItem(item.apiId);
        },
      })),
    [filteredRecentItems]
  );

  const actions = useMemo((): Array<OmniboxActionItem> => {
    // During filter mode, show filter suggestions
    if (filterMode != null) {
      return typeaheadActions;
    }

    if (results) {
      return buildActions(results, copiedAnswer, handleCopyAnswer, helpDocsResults);
    }

    if (!query && selectedFilters.length > 0) {
      if (filteredRecentLoading) {
        return [
          {
            id: 'filtered-recent-loading',
            title: 'Loading...',
            group: 'Recently Modified',
            resultType: 'filtered_recent_loading',
            onTrigger: noop,
          },
        ];
      }
      if (filteredRecentActions.length === 0) {
        return [
          {
            id: 'filtered-recent-empty',
            title: 'No items match the selected filters',
            group: 'Recently Modified',
            resultType: 'filtered_recent_empty',
            onTrigger: noop,
          },
        ];
      }
      return filteredRecentActions;
    }

    if (!query) {
      return defaultActions;
    }

    return [];
  }, [
    filterMode,
    typeaheadActions,
    results,
    query,
    selectedFilters.length,
    copiedAnswer,
    handleCopyAnswer,
    defaultActions,
    filteredRecentActions,
    filteredRecentLoading,
    helpDocsResults,
  ]);

  const hasNavigableResults = actions.length > 0 && filterMode == null;

  const handleClose = useCallback(
    (closeMethod: 'escape' | 'clicked_result' | 'clicked_outside' = 'clicked_outside') => {
      trackEvent(OMNIBOX_ANALYTICS_ACTIONS.CLOSED_OMNIBOX, {
        closeMethod,
        queriesSubmitted: queriesSubmittedRef.current,
        hasResults: resultsRef.current != null,
        resultCount: resultsRef.current
          ? resultsRef.current.searchResults.length + resultsRef.current.contentResults.length
          : 0,
        filterCount: selectedFilters.length,
        usedArrowKeys: usedArrowKeysRef.current,
      });
      usedArrowKeysRef.current = false;
      close();
      requestIdRef.current = null;
      lastSubmittedQueryRef.current = null;
      abortAttachmentPreview();
      setResults(null);
      setLoading(false);
      setQuery('');
      setSelectedFilters([]);
      exitFilterMode();
      setShowCompletionFlash(false);
      setHelpDocsResults(new Map());
      setErrorMessage(null);
    },
    [close, setResults, setHelpDocsResults, requestIdRef, exitFilterMode, selectedFilters.length]
  );

  const executeSearch = useCallback(
    (searchQuery: string, filters: Array<OmniboxFilter>, {isRequery}: {isRequery: boolean}) => {
      const queryWords = searchQuery.trim().split(/\s+/).length;
      if (!isRequery) {
        queriesSubmittedRef.current += 1;
        trackEvent(OMNIBOX_ANALYTICS_ACTIONS.SUBMITTED_QUERY, {
          queryLength: searchQuery.length,
          queryWords,
          filterCount: filters.length,
        });
      }

      const {activeId, workspaceItems} = WorkspaceStore.getState();
      setLoading(true);
      setResults(null);
      setHelpDocsResults(new Map());

      const requestId = crypto.randomUUID();
      requestIdRef.current = requestId;
      setLastRequestId(requestId);
      lastSubmittedQueryRef.current = searchQuery;
      setErrorMessage(null);

      const {schemaApiIds, objectTypes, projectApiIds, folderApiIds, authorApiIds} =
        extractFilterParams(filters);

      cleanUpOmniboxSearchProfilerEvents();
      logProfilerTimingEvent({...OMNIBOX_SEARCH_STARTED_EVENT, id: requestId});
      streamOmniboxQuery({
        query: searchQuery,
        requestId,
        activeWorkspaceTabId: activeId ?? undefined,
        openWorkspaceTabIds: workspaceItems.map((item) => item.fileId),
        schemaApiIds: schemaApiIds.length > 0 ? schemaApiIds : undefined,
        objectTypes: objectTypes.length > 0 ? objectTypes : undefined,
        projectApiIds: projectApiIds.length > 0 ? projectApiIds : undefined,
        folderApiIds: folderApiIds.length > 0 ? folderApiIds : undefined,
        authorApiIds: authorApiIds.length > 0 ? authorApiIds : undefined,
      })
        .then((finalResults) => {
          if (mountedRef.current && requestIdRef.current === requestId) {
            setResults(finalResults);
            setLoading(false);
            trackEvent(OMNIBOX_ANALYTICS_ACTIONS.DISPLAYED_RESULTS, {
              queryLength: searchQuery.length,
              queryWords,
              searchResultCount: finalResults.searchResults.length,
              contentResultCount: finalResults.contentResults.length,
              askSuggestionCount: finalResults.askSuggestions.length,
              hasDirectAnswer: finalResults.directAnswer != null,
            });
          }
        })
        .catch((e) => {
          // eslint-disable-next-line no-console
          console.error('Failed to stream omnibox query', e);
          if (mountedRef.current && requestIdRef.current === requestId) {
            setLoading(false);
            setErrorMessage('Search failed. Please try again.');
            // Allow retrying the same query after failure
            lastSubmittedQueryRef.current = null;
          }
        });
    },
    [setResults, setHelpDocsResults, requestIdRef]
  );

  // Fire the rendered timing event on the first transition from null → non-null results per request
  const lastLoggedRequestIdRef = useRef<string | null>(null);
  useEffect(() => {
    if (results != null && lastRequestId != null && lastLoggedRequestIdRef.current !== lastRequestId) {
      lastLoggedRequestIdRef.current = lastRequestId;
      logProfilerTimingEvent({...OMNIBOX_SEARCH_RESULTS_RENDERED_EVENT, id: lastRequestId});
    }
  }, [results, lastRequestId]);

  // Automatically re-execute the query when filters change while results are showing.
  // This fires when the user adds/removes a filter chip after having already submitted a search.
  const prevFiltersRef = useRef(selectedFilters);
  useEffect(() => {
    const filtersChanged = prevFiltersRef.current !== selectedFilters;
    prevFiltersRef.current = selectedFilters;

    if (filtersChanged && lastSubmittedQueryRef.current != null && filterMode == null) {
      executeSearch(lastSubmittedQueryRef.current, selectedFilters, {isRequery: true});
    }
  }, [selectedFilters, filterMode, executeSearch]);

  const handleKeyDownCapture = useCallback(
    (event: React.KeyboardEvent<HTMLDivElement>) => {
      // Backspace in filter mode with empty typeahead exits filter mode;
      // backspace with empty query removes the last filter chip
      if (event.key === 'Backspace') {
        if (filterMode != null && !typeaheadQuery) {
          exitFilterMode();
          return;
        }
        if (filterMode == null && !query && selectedFilters.length > 0) {
          trackEvent(OMNIBOX_ANALYTICS_ACTIONS.REMOVED_FILTER, {
            filterKind: selectedFilters[selectedFilters.length - 1].kind,
          });
          setSelectedFilters((prev) => prev.slice(0, -1));
          return;
        }
      }

      // Escape during filter mode exits it; otherwise closes the modal
      if (event.key === 'Escape') {
        event.stopPropagation();
        if (filterMode != null) {
          exitFilterMode();
        } else {
          handleClose('escape');
        }
        return;
      }

      // In filter mode, let keyboard nav handle arrow keys and Enter
      if (filterMode != null) {
        return;
      }

      // Deduplicate consecutive identical submissions; ref is cleared on failure and reopen to allow retry
      if (event.key === 'Enter' && query && query !== lastSubmittedQueryRef.current) {
        event.preventDefault();
        event.stopPropagation();

        executeSearch(query, selectedFilters, {isRequery: false});
      }
    },
    [query, selectedFilters, filterMode, typeaheadQuery, exitFilterMode, handleClose, executeSearch]
  );

  const {
    hoveredIndex,
    handleKeyDown: handleNavKeyDown,
    handleMouseEnter,
    handleMouseMove,
    scrollContainerRef,
  } = useOmniboxKeyboardNav({
    actions,
    enabled: actions.length > 0,
    onClose: () => {
      handleClose('clicked_result');
    },
    onSelect: (action, index) => {
      trackEvent(OMNIBOX_ANALYTICS_ACTIONS.CLICKED_RESULT, {
        resultType: (action as OmniboxActionItem).resultType,
        index,
        objectType: (action as OmniboxActionItem).objectType,
        group: (action as OmniboxActionItem).group,
        filterCount: selectedFilters.length,
        selectionMethod: 'keyboard',
      });
    },
    usedArrowKeys: usedArrowKeysRef,
  });

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLDivElement>) => {
      const isInputFocused = event.target === inputRef.current;

      // Escape should work from any focused element
      if (event.key === 'Escape') {
        handleKeyDownCapture(event);
        return;
      }

      // All other custom keyboard handling only applies when the input is focused.
      // Let buttons and other interactive elements handle their own keys natively.
      if (!isInputFocused) {
        return;
      }

      handleKeyDownCapture(event);
      if (!event.defaultPrevented) {
        handleNavKeyDown(event);
      }
    },
    [handleKeyDownCapture, handleNavKeyDown]
  );

  const handleInputChange = useCallback(
    (value: string) => {
      if (filterMode != null) {
        setTypeaheadQuery(value);
      } else {
        setQuery(value);
        setErrorMessage(null);
        // When the input is cleared, reset search state immediately to prevent
        // an in-flight request from overwriting the cleared UI with stale results.
        if (!value) {
          requestIdRef.current = null;
          lastSubmittedQueryRef.current = null;
          setResults(null);
          setLoading(false);
        } else if (results && !loading) {
          setResults(null);
          lastSubmittedQueryRef.current = null;
        }
      }
    },
    [filterMode, results, loading, setResults, requestIdRef]
  );

  const handleFilterTabChange = useCallback(
    (tab: FilterTab) => {
      const newMode = tab === 'all' ? null : tab;
      if (newMode != null && newMode === filterMode) {
        exitFilterMode();
      } else {
        setFilterMode(newMode);
        setTypeaheadQuery('');
      }
      inputRef.current?.focus();
    },
    [filterMode, exitFilterMode]
  );

  const showDefaultState = !query && !results && filterMode == null && selectedFilters.length === 0;

  const nothingFoundMessage = filterMode != null ? NOTHING_FOUND_MESSAGES[filterMode] : null;

  // Group actions by group name for section headers.
  // Uses a Map so that items with the same group name are merged even when non-consecutive
  // (e.g. interleaved project/folder items from the API), avoiding duplicate React keys.
  const groupedActions = useMemo(() => {
    const groupMap = new Map<
      string,
      {group: string; actions: Array<{action: OmniboxActionItem; index: number}>}
    >();
    actions.forEach((action, index) => {
      const group = action.group ?? '';
      let entry = groupMap.get(group);
      if (!entry) {
        entry = {group, actions: []};
        groupMap.set(group, entry);
      }
      entry.actions.push({action, index});
    });
    return [...groupMap.values()];
  }, [actions]);

  // Live region text for screen reader announcements
  const statusMessage = useMemo(() => {
    if (errorMessage) {
      return errorMessage;
    }
    if (loading) {
      return 'Searching...';
    }
    if (filterMode != null && actions.length === 0 && nothingFoundMessage) {
      return nothingFoundMessage;
    }
    if (results) {
      const count = actions.length;
      if (count === 0) {
        return 'No results found';
      }
      return `${count} result${count !== 1 ? 's' : ''} found`;
    }
    return '';
  }, [loading, errorMessage, filterMode, actions.length, nothingFoundMessage, results]);

  const placeholder =
    filterMode != null
      ? FILTER_MODE_PLACEHOLDERS[filterMode]
      : selectedFilters.length > 0
        ? 'Search within selected filters...'
        : 'Type and press enter to search';

  const composeEnabled = isAgentTypeEnabled(
    BenchlingConfig.LLM_ENABLE_ASSISTANT_AGENT ? AgentType.ASSISTANT : AgentType.DATA_CAPTURE
  );

  return (
    <>
      <Modal
        opened={opened}
        onClose={handleClose}
        withCloseButton={false}
        aria-label="Command palette"
        size={1060}
        radius="md"
        centered={false}
        closeOnClickOutside={true}
        overlayProps={{opacity: 0.3}}
        trapFocus={true}
        styles={{
          root: {zIndex: 1055},
          overlay: {zIndex: 1054},
          inner: {
            zIndex: 1055,
            paddingTop: 'clamp(20px, 8vh, 120px)',
            paddingBottom: 'clamp(20px, 8vh, 120px)',
          },
          // !important needed to override nucleus-ui MODAL_DEFAULTS which sets body padding/content flex with !important
          content: {
            overflow: 'hidden !important' as never,
            maxHeight: '100%',
            display: 'flex',
            flexDirection: 'column',
          },
          header: {display: 'none'},
          body: {
            padding: '0 !important' as never,
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
          },
        }}
      >
        {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions -- keyboard events delegated from child input */}
        <div onKeyDown={handleKeyDown} data-omnibox-content="" className={classes.contentWrapper}>
          <div aria-live="polite" aria-atomic="true" className={classes.srOnly}>
            {statusMessage}
          </div>
          <OmniboxSearchInput
            query={filterMode != null ? typeaheadQuery : query}
            onQueryChange={handleInputChange}
            placeholder={placeholder}
            inputRef={inputRef}
            listboxId="omnibox-listbox"
            expanded={actions.length > 0}
            activeDescendantId={
              hoveredIndex >= 0 && actions.length > 0 ? `omnibox-option-${hoveredIndex}` : undefined
            }
          />

          {/* Filter chips row */}
          {selectedFilters.length > 0 && (
            <div className={classes.filterChipsRow}>
              {selectedFilters.map((filter) => (
                <Chip
                  key={`${filter.kind}:${filter.id}`}
                  text={getFilterChipLabel(filter)}
                  size={ChipSize.MINI}
                  onRemove={() => {
                    trackEvent(OMNIBOX_ANALYTICS_ACTIONS.REMOVED_FILTER, {filterKind: filter.kind});
                    setSelectedFilters((prev) =>
                      prev.filter((f) => !(f.kind === filter.kind && f.id === filter.id))
                    );
                  }}
                />
              ))}
              {selectedFilters.length > 1 && (
                <UnstyledButton className={classes.clearAllButton} onClick={handleClearAllFilters}>
                  Clear all
                </UnstyledButton>
              )}
            </div>
          )}

          <OmniboxFilterTabs activeTab={activeTab} onTabChange={handleFilterTabChange} />

          {/* Scrollable content area — grows with content, scrolls only when viewport is full */}
          <div ref={scrollContainerRef} onMouseMove={handleMouseMove} className={classes.scrollArea}>
            {showDefaultState && (
              <OmniboxQuickActions
                composeEnabled={composeEnabled}
                onEntryFromTemplate={() => {
                  trackEvent(OMNIBOX_ANALYTICS_ACTIONS.CLICKED_QUICK_ACTION, {
                    quickAction: 'entry_from_template',
                  });
                  handleClose('clicked_result');
                  showCreateEntryFromTemplateModal({});
                }}
                onEntryFromCompose={() => {
                  trackEvent(OMNIBOX_ANALYTICS_ACTIONS.CLICKED_QUICK_ACTION, {
                    quickAction: 'entry_from_compose',
                  });
                  handleClose('clicked_result');
                  openDraftChatInSidepanel(
                    BenchlingConfig.LLM_ENABLE_ASSISTANT_AGENT ? AgentType.ASSISTANT : AgentType.DATA_CAPTURE
                  );
                }}
                onOpenReviews={() => {
                  trackEvent(OMNIBOX_ANALYTICS_ACTIONS.CLICKED_QUICK_ACTION, {quickAction: 'open_reviews'});
                  handleClose('clicked_result');
                  if (taskData?.currentUser?.id) {
                    setTaskSearchFilter(taskData.currentUser.id, null);
                  }
                }}
              />
            )}

            {/* Error message */}
            {errorMessage && (
              <div className={classes.errorMessage} role="alert">
                {errorMessage}
              </div>
            )}

            {/* Nothing found message for typeahead */}
            {filterMode != null && actions.length === 0 && nothingFoundMessage && (
              <div className={classes.emptyMessage} role="status">
                {nothingFoundMessage}
              </div>
            )}

            {/* Action groups */}
            <div role="listbox" id="omnibox-listbox" aria-label="Results">
              {groupedActions.map((group) => (
                <div key={group.group} role="group" aria-label={group.group || undefined}>
                  {group.group && (
                    <div className={classes.sectionHeader} aria-hidden="true">
                      {group.group === 'Ask Benchling' && (
                        <BenchlingIcon icon="sparkle" style={{fontSize: 12, marginRight: 4}} />
                      )}
                      {group.group}
                    </div>
                  )}
                  {group.actions.map(({action, index}) => {
                    const isNonInteractive =
                      action.resultType === 'filtered_recent_loading' ||
                      action.resultType === 'filtered_recent_empty' ||
                      action.resultType === 'help_docs_loading';
                    return (
                      <div
                        key={action.id}
                        id={`omnibox-option-${index}`}
                        role="option"
                        aria-selected={hoveredIndex === index}
                        aria-disabled={isNonInteractive || undefined}
                        tabIndex={-1}
                        data-action-index={index}
                        onMouseEnter={() => {
                          handleMouseEnter(index);
                        }}
                      >
                        <OmniboxAction
                          action={action}
                          hovered={hoveredIndex === index}
                          onTrigger={() => {
                            trackEvent(OMNIBOX_ANALYTICS_ACTIONS.CLICKED_RESULT, {
                              resultType: action.resultType,
                              index,
                              objectType: action.objectType,
                              group: action.group,
                              filterCount: selectedFilters.length,
                              selectionMethod: 'mouse',
                            });
                            action.onTrigger();
                            if (action.closeOnTrigger !== false) {
                              handleClose('clicked_result');
                            }
                          }}
                        />
                      </div>
                    );
                  })}
                </div>
              ))}
            </div>
          </div>

          <OmniboxFooter
            hasNavigableResults={hasNavigableResults}
            query={query}
            requestId={requestIdRef.current}
            hasDirectAnswer={results?.directAnswer != null}
            searchResultCount={results?.searchResults.length ?? 0}
            filterOptionCount={totalFilterOptionCount}
            filterShownCount={filterMode != null ? actions.length : undefined}
          />
        </div>
      </Modal>
      {opened && (loading || showCompletionFlash) && (
        <AnimatedBorderOverlay loading={loading} showCompletionFlash={showCompletionFlash} />
      )}
    </>
  );
}
